<template>
  <section class="estimate-page">
    <header class="estimate-header">
      <div>
        <h1>견적서</h1>
        <p>작성, 발행내역, 거래처, 공급자를 분리해서 관리합니다.</p>
      </div>
      <div class="actions">
        <button type="button" class="secondary" @click="resetForm">새 견적</button>
        <button type="button" class="primary" :disabled="loading" @click="renderEstimate">
          {{ loading ? "처리 중" : editingRecordId ? "수정 저장/보기" : "견적서 발행" }}
        </button>
      </div>
    </header>

    <nav class="estimate-tabs">
      <button
        v-for="tab in tabs"
        :key="tab.key"
        type="button"
        :class="{ active: activeTab === tab.key }"
        @click="activeTab = tab.key"
      >
        {{ tab.label }}
      </button>
    </nav>

    <div v-if="activeTab === 'write'" class="estimate-layout">
      <form class="estimate-form" @submit.prevent="renderEstimate">
        <section class="form-band">
          <h2>기본 정보</h2>
          <div class="field-grid">
            <label>
              <span>No.</span>
              <div class="readonly-line">{{ form.no }}</div>
            </label>
            <label>
              <span>견적일</span>
              <div class="readonly-line">{{ form.date }}</div>
            </label>
            <label>
              <span>수신</span>
              <input v-model="form.receiver" type="text" />
            </label>
            <label>
              <span>유효기간</span>
              <div class="readonly-line">{{ form.validPeriod }}</div>
            </label>
          </div>
        </section>

        <section class="form-band">
          <div class="section-title-row">
            <h2>공급자</h2>
            <button type="button" class="secondary compact" @click="activeTab = 'suppliers'">
              공급자 관리
            </button>
          </div>
          <div class="party-select">
            <label>
              <span>저장된 공급자</span>
              <select v-model="selectedSupplierId" @change="applyParty('supplier', $event.target.value)">
                <option value="">선택</option>
                <option v-for="party in suppliers" :key="party.id" :value="party.id">
                  {{ party.name }}
                </option>
              </select>
            </label>
          </div>
          <div class="supplier-summary">
            <div>
              <span>상호</span>
              <strong>{{ form.supplier.company }}</strong>
            </div>
            <div>
              <span>등록번호</span>
              <strong>{{ form.supplier.regNo }}</strong>
            </div>
            <div>
              <span>대표</span>
              <strong>{{ form.supplier.ceo }}</strong>
            </div>
            <div class="wide">
              <span>계좌</span>
              <strong>{{ form.supplier.bank }}</strong>
            </div>
          </div>
        </section>

        <section class="form-band">
          <div class="section-title-row">
            <h2>품목</h2>
            <button type="button" class="secondary compact" @click="addItem">품목 추가</button>
          </div>
          <div class="item-table">
            <div class="item-head">
              <span>품목</span>
              <span>규격</span>
              <span>수량</span>
              <span>단위</span>
              <span>단가</span>
              <span>비고</span>
              <span></span>
            </div>
            <div v-for="(item, index) in form.items" :key="index" class="item-row">
              <input v-model="item.name" type="text" :readonly="index === 0" :class="{ locked: index === 0 }" />
              <input v-model="item.spec" type="text" :readonly="index === 0" :class="{ locked: index === 0 }" />
              <input v-model.number="item.qty" type="number" min="0" step="1" :readonly="index === 0" :class="{ locked: index === 0 }" />
              <input v-model="item.unit" type="text" :readonly="index === 0" :class="{ locked: index === 0 }" />
              <input v-model.number="item.price" type="number" min="0" step="1000" />
              <input v-model="item.memo" type="text" />
              <button type="button" class="icon-button" :disabled="index === 0" @click="removeItem(index)">×</button>
            </div>
            <label class="cut-option">
              <input v-model="form.roundDownBelowTenThousand" type="checkbox" />
              <span>견적서 10번에 천원단위 절삭 표시</span>
            </label>
          </div>
        </section>

        <section class="form-band">
          <h2>하단 정보</h2>
          <div class="field-grid">
            <label class="wide">
              <span>계좌정보</span>
              <div class="readonly-line">{{ form.supplier.bank }}</div>
            </label>
            <label>
              <span>담당자</span>
              <div class="readonly-line">{{ form.manager }}</div>
            </label>
            <label class="wide">
              <span>비고</span>
              <div class="readonly-line">{{ form.memo }}</div>
            </label>
            <label class="wide">
              <span>비고 추가</span>
              <input v-model="form.memo2" type="text" />
            </label>
          </div>
        </section>
      </form>

      <aside class="total-panel">
        <h2>합계</h2>
        <dl>
          <div>
            <dt>공급가액</dt>
            <dd>{{ money(supply) }} 원</dd>
          </div>
          <div>
            <dt>VAT</dt>
            <dd>{{ money(vat) }} 원</dd>
          </div>
          <div class="grand">
            <dt>합계</dt>
            <dd>{{ money(total) }} 원</dd>
          </div>
        </dl>
      </aside>
    </div>

    <section v-else-if="activeTab === 'records'" class="form-band">
      <div class="section-title-row">
        <h2>발행 견적서</h2>
        <button type="button" class="secondary compact" @click="loadRecords">새로고침</button>
      </div>
      <div class="record-filters">
        <input v-model="recordQuery" type="text" placeholder="번호, 거래처, 공급자 검색" @keyup.enter="loadRecords" />
        <select v-model="recordStatus" @change="loadRecords">
          <option value="">전체 상태</option>
          <option value="issued">발행</option>
          <option value="cancelled">취소</option>
          <option value="deleted">삭제</option>
        </select>
        <button type="button" class="secondary" @click="loadRecords">검색</button>
      </div>
      <div class="records-table">
        <div class="records-head">
          <span>No.</span>
          <span>일자</span>
          <span>거래처</span>
          <span>금액</span>
          <span>상태</span>
          <span></span>
        </div>
        <div v-for="record in records" :key="record.id" class="records-row">
          <strong data-label="No.">{{ record.no }}</strong>
          <span class="date-cell" data-label="일자">
            <strong>{{ record.date }}</strong>
            <small v-if="record.createdAt">발행 {{ compactDateTime(record.createdAt) }}</small>
          </span>
          <span data-label="거래처">{{ record.customerName || "-" }}</span>
          <span data-label="금액">{{ money(record.totalAmount) }} 원</span>
          <span class="status-cell" data-label="상태">
            <span :class="['status-pill', record.status, { statement: record.statementIssued }]">
              {{ statusLabel(record) }}
            </span>
            <small v-if="record.statementIssuedAt">{{ compactDateTime(record.statementIssuedAt) }}</small>
          </span>
          <div class="row-actions">
            <button type="button" class="secondary compact action-short" @click="viewEstimate(record)">견적서</button>
            <button type="button" class="secondary compact action-short" @click="editRecord(record.id)">수정</button>
            <button
              type="button"
              class="secondary compact action-wide"
              :disabled="record.status !== 'issued'"
              @click="issueStatement(record)"
            >
              거래명세서
            </button>
            <button
              type="button"
              class="danger compact action-short"
              :disabled="record.status === 'cancelled'"
              @click="cancelRecord(record)"
            >
              취소
            </button>
            <button
              type="button"
              class="danger compact action-short"
              :disabled="record.status === 'deleted'"
              @click="deleteRecord(record)"
            >
              숨김
            </button>
          </div>
        </div>
        <p v-if="!records.length" class="empty-text">발행된 견적서가 없습니다.</p>
      </div>
    </section>

    <section v-else-if="activeTab === 'customers'" class="form-band">
      <party-manager
        title="거래처 등록"
        type="customer"
        :items="customers"
        :blank="blankCustomerParty()"
        @save="saveManagedParty"
        @delete="deleteParty"
      />
    </section>

    <section v-else-if="activeTab === 'suppliers'" class="form-band">
      <party-manager
        title="공급자 등록"
        type="supplier"
        :items="suppliers"
        :blank="blankSupplierParty()"
        @save="saveManagedParty"
        @delete="deleteParty"
      />
    </section>
  </section>
</template>

<script setup>
import { computed, defineComponent, h, onMounted, reactive, ref } from "vue";
import api from "@/api";
import defaultStampUrl from "@/images/geenie.png";

function defaultStampImage() {
  if (typeof window === "undefined") return defaultStampUrl;
  return new URL(defaultStampUrl, window.location.origin).href;
}

const PartyManager = defineComponent({
  name: "PartyManager",
  props: {
    title: { type: String, required: true },
    type: { type: String, required: true },
    items: { type: Array, required: true },
    blank: { type: Object, required: true },
  },
  emits: ["save", "delete"],
  data() {
    return { form: { ...this.blank }, selectedId: "" };
  },
  watch: {
    blank() {
      if (!this.selectedId) this.form = { ...this.blank };
    },
  },
  methods: {
    selectParty(id) {
      this.selectedId = id;
      const party = this.items.find((item) => String(item.id) === String(id));
      this.form = party ? { ...party } : { ...this.blank };
    },
    reset() {
      this.selectedId = "";
      this.form = { ...this.blank };
    },
    save() {
      this.$emit("save", { ...this.form, partyType: this.type });
      this.reset();
    },
    remove() {
      if (!this.selectedId) return;
      this.$emit("delete", Number(this.selectedId));
      this.reset();
    },
    setStamp(event) {
      const file = event.target.files?.[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = () => {
        this.form.stampImage = String(reader.result || "");
      };
      reader.readAsDataURL(file);
    },
    clearStamp() {
      this.form.stampImage = "";
    },
  },
  render() {
    const isSupplier = this.type === "supplier";
    const field = (label, key, wide = false) =>
      h("label", { class: wide ? "wide" : "" }, [
        h("span", label),
        h("input", {
          value: this.form[key] || "",
          onInput: (event) => (this.form[key] = event.target.value),
        }),
      ]);
    return h("div", { class: "manager-panel" }, [
      h("div", { class: "section-title-row" }, [
        h("h2", this.title),
        h("button", { type: "button", class: "secondary compact", onClick: this.reset }, isSupplier ? "새 공급자" : "새 거래처"),
        h("button", { type: "button", class: "primary compact", onClick: this.save }, "저장"),
        h("button", { type: "button", class: "danger compact", disabled: !this.selectedId, onClick: this.remove }, "숨김"),
      ]),
      h("div", { class: "manager-grid" }, [
        h("aside", { class: "manager-side" }, [
          h("label", [
            h("span", "저장된 항목"),
            h(
              "select",
              { value: this.selectedId, onChange: (event) => this.selectParty(event.target.value) },
              [
                h("option", { value: "" }, "신규 입력"),
                ...this.items.map((party) => h("option", { value: party.id }, party.name)),
              ]
            ),
          ]),
          h("div", { class: "manager-count" }, `${this.items.length}개 저장됨`),
          h(
            "div",
            { class: "manager-list" },
            this.items.map((party) =>
              h(
                "button",
                {
                  type: "button",
                  class: ["manager-list-item", { active: String(this.selectedId) === String(party.id) }],
                  onClick: () => this.selectParty(party.id),
                },
                [h("strong", party.name || "-"), h("span", party.regNo || party.phone || party.bank || "")]
              )
            )
          ),
        ]),
        h("div", { class: "manager-form field-grid supplier-grid" }, [
          field("상호", "name"),
          field("등록번호", "regNo"),
          field("대표", "ceo"),
          field("담당자", "contactName"),
          field("연락처", "phone"),
          field("업태", "businessType"),
          field("주소", "address", true),
          field("이메일", "email"),
          isSupplier ? field("계좌", "bank", true) : null,
          isSupplier
            ? h("div", { class: "stamp-editor wide" }, [
                h("div", { class: "stamp-title" }, "도장"),
                this.form.stampImage
                  ? h("img", { class: "stamp-preview-img", src: this.form.stampImage, alt: "도장 미리보기" })
                  : h("div", { class: "stamp-empty" }, "등록된 도장 없음"),
                h("div", { class: "stamp-actions" }, [
                  h("input", { type: "file", accept: "image/*", onChange: this.setStamp }),
                  h("button", { type: "button", class: "secondary compact", onClick: () => (this.form.stampImage = defaultStampImage()) }, "기본 도장"),
                  h("button", { type: "button", class: "danger compact", onClick: this.clearStamp }, "제거"),
                ]),
              ])
            : null,
          field("메모", "memo", true),
          h("label", [
            h("span", "기본값"),
            h("select", {
              value: this.form.isDefault ? "1" : "0",
              onChange: (event) => (this.form.isDefault = event.target.value === "1"),
            }, [h("option", { value: "0" }, "아니오"), h("option", { value: "1" }, "예")]),
          ]),
        ]),
      ]),
    ]);
  },
});

const tabs = [
  { key: "write", label: "견적서 작성" },
  { key: "records", label: "발행내역" },
  { key: "customers", label: "거래처 등록" },
  { key: "suppliers", label: "공급자 등록/변경" },
];

const activeTab = ref("write");
const loading = ref(false);
const suppliers = ref([]);
const customers = ref([]);
const records = ref([]);
const recordQuery = ref("");
const recordStatus = ref("");
const noTouched = ref(false);
const editingRecordId = ref(null);
const selectedSupplierId = ref("");

function todayKoreanDate() {
  const now = new Date();
  return `${now.getFullYear()}년 ${now.getMonth() + 1}월 ${now.getDate()}일`;
}

function createDefaultForm() {
  return {
    no: "",
    date: todayKoreanDate(),
    receiver: "",
    validPeriod: "발행일로부터 30일",
    supplier: {
      regNo: "666-02-03193",
      ceo: "임진남",
      company: "지니 통신",
      address: "인천광역시 동구 새천년로38번길 11, 206-1102",
      phone: "代) 010-2786-5741",
      businessType: "통신",
      bank: "카카오뱅크 3333-19-4950241 임진남",
    },
    customer: {
      company: "",
      contactName: "",
      regNo: "",
      ceo: "",
      address: "",
      phone: "",
      email: "",
      businessType: "",
      memo: "",
    },
    items: [{ name: "네트워크", spec: "식", qty: 1, unit: "EA", price: null, memo: "" }],
    cutLabel: "",
    cutAmount: 0,
    roundDownBelowTenThousand: true,
    manager: "최일도(010-2786-5741)",
    memo: "2026년 통신케이블 노임단가 436,224원",
    memo2: "",
    stampImage: defaultStampImage(),
  };
}

const form = reactive(createDefaultForm());

onMounted(async () => {
  await loadParties();
  await refreshEstimateNo();
  await loadRecords();
});

const supply = computed(
  () => form.items.reduce((sum, item) => sum + Number(item.qty || 0) * Number(item.price || 0), 0) + Number(form.cutAmount || 0)
);
const vat = computed(() => Math.round(supply.value * 0.1));
const total = computed(() => supply.value + vat.value);

function money(value) {
  return Number(value || 0).toLocaleString("ko-KR");
}

function compactDateTime(value) {
  if (!value) return "";
  const text = String(value).replace("T", " ");
  const match = text.match(/^(\d{4})-(\d{2})-(\d{2})\s+(\d{2}):(\d{2})/);
  if (!match) return text.slice(0, 16);
  return `${match[1]}-${match[2]}-${match[3]} ${match[4]}:${match[5]}`;
}

function statusLabel(record) {
  if (record.status === "deleted") return "삭제";
  if (record.status === "cancelled") return "취소";
  return record.statementIssued ? "거래명세서 발행" : "견적서 발행";
}

function addItem() {
  if (form.items.length >= 10) return;
  form.items.push({ name: "", spec: "", qty: 1, unit: "EA", price: null, memo: "" });
}

function removeItem(index) {
  if (form.items.length <= 1) return;
  form.items.splice(index, 1);
}

function resetForm() {
  Object.assign(form, createDefaultForm());
  editingRecordId.value = null;
  noTouched.value = false;
  activeTab.value = "write";
  loadParties().then(refreshEstimateNo);
}

function syncReceiver() {
  form.receiver = `${form.customer.company || ""} ${form.customer.contactName || ""}`.trim();
}

function blankCustomerParty() {
  return {
    id: 0,
    partyType: "customer",
    name: "",
    regNo: "",
    ceo: "",
    contactName: "",
    address: "",
    phone: "",
    email: "",
    businessType: "",
    bank: "",
    stampImage: "",
    memo: "",
    isDefault: false,
  };
}

function blankSupplierParty() {
  return {
    ...blankCustomerParty(),
    partyType: "supplier",
    name: "",
    bank: "",
    stampImage: defaultStampImage(),
  };
}

async function loadParties() {
  const [supplierRes, customerRes] = await Promise.all([
    api.get("/estimate/parties", { params: { type: "supplier" } }),
    api.get("/estimate/parties", { params: { type: "customer" } }),
  ]);
  suppliers.value = supplierRes.data || [];
  customers.value = customerRes.data || [];
  const defaultSupplier = suppliers.value.find((party) => party.isDefault);
  if (!editingRecordId.value && defaultSupplier) {
    selectedSupplierId.value = String(defaultSupplier.id);
    applyPartyObject("supplier", defaultSupplier);
  }
}

async function refreshEstimateNo() {
  if (editingRecordId.value) return;
  try {
    const response = await api.get("/estimate/next-no", { params: { date: form.date } });
    if (response.data?.no && (!noTouched.value || !String(form.no || "").trim())) {
      form.no = response.data.no;
    }
  } catch (error) {
    console.error(error);
  }
}

function applyParty(type, id) {
  if (!id) return;
  const list = type === "supplier" ? suppliers.value : customers.value;
  const party = list.find((item) => String(item.id) === String(id));
  if (party) {
    if (type === "supplier") selectedSupplierId.value = String(party.id);
    applyPartyObject(type, party);
  }
}

function applyPartyObject(type, party) {
  if (type === "supplier") {
    form.supplier.regNo = party.regNo || "";
    form.supplier.ceo = party.ceo || "";
    form.supplier.company = party.name || "";
    form.supplier.address = party.address || "";
    form.supplier.phone = party.phone || "";
    form.supplier.businessType = party.businessType || "";
    form.supplier.bank = party.bank || createDefaultForm().supplier.bank;
    form.stampImage = party.stampImage || defaultStampImage();
    return;
  }
  form.customer.company = party.name || "";
  form.customer.contactName = party.contactName || "";
  form.customer.regNo = party.regNo || "";
  form.customer.ceo = party.ceo || "";
  form.customer.address = party.address || "";
  form.customer.phone = party.phone || "";
  form.customer.email = party.email || "";
  form.customer.businessType = party.businessType || "";
  form.customer.memo = party.memo || "";
  syncReceiver();
}

async function saveManagedParty(payload) {
  try {
    if (!String(payload.name || "").trim()) {
      alert("상호를 입력해 주세요.");
      return;
    }
    if (payload.id) {
      await api.put(`/estimate/parties/${payload.id}`, payload);
    } else {
      await api.post("/estimate/parties", payload);
    }
    await loadParties();
    alert("저장했습니다.");
  } catch (error) {
    console.error(error);
    alert("저장 중 오류가 발생했습니다.");
  }
}

async function deleteParty(id) {
  if (!confirm("삭제하면 목록에서 숨겨집니다. 진행할까요?")) return;
  try {
    await api.delete(`/estimate/parties/${id}`);
    await loadParties();
  } catch (error) {
    console.error(error);
    alert("삭제 중 오류가 발생했습니다.");
  }
}

async function loadRecords() {
  try {
    const response = await api.get("/estimate/records", {
      params: { q: recordQuery.value || undefined, status: recordStatus.value || undefined },
    });
    records.value = response.data || [];
  } catch (error) {
    console.error(error);
  }
}

function applyRecordPayload(payload, keepNo = true) {
  Object.assign(form, createDefaultForm());
  form.no = keepNo ? payload.no || "" : "";
  form.date = payload.date || todayKoreanDate();
  form.receiver = payload.receiver || "";
  form.validPeriod = payload.validPeriod || "발행일로부터 30일";
  form.supplier = { ...createDefaultForm().supplier, ...(payload.supplier || {}) };
  syncSelectedSupplier();
  form.customer = { ...createDefaultForm().customer, ...(payload.customer || {}) };
  form.items = Array.isArray(payload.items) && payload.items.length ? payload.items : createDefaultForm().items;
  form.cutLabel = payload.cutLabel || "";
  form.cutAmount = payload.cutAmount || 0;
  form.roundDownBelowTenThousand = payload.roundDownBelowTenThousand !== false;
  form.manager = payload.manager || createDefaultForm().manager;
  form.memo = payload.memo || "";
  form.memo2 = payload.memo2 || "";
  form.stampImage = payload.stampImage || defaultStampImage();
}

function syncSelectedSupplier() {
  const supplier = suppliers.value.find((party) => String(party.name || "") === String(form.supplier.company || ""));
  selectedSupplierId.value = supplier ? String(supplier.id) : "";
}

async function editRecord(id) {
  try {
    const response = await api.get(`/estimate/records/${id}`);
    applyRecordPayload(response.data.payload, true);
    editingRecordId.value = id;
    activeTab.value = "write";
  } catch (error) {
    console.error(error);
    alert("견적서를 불러오지 못했습니다.");
  }
}

async function copyRecord(id) {
  try {
    const response = await api.get(`/estimate/records/${id}`);
    applyRecordPayload(response.data.payload, false);
    editingRecordId.value = null;
    noTouched.value = false;
    activeTab.value = "write";
    await refreshEstimateNo();
  } catch (error) {
    console.error(error);
    alert("견적서를 복사하지 못했습니다.");
  }
}

async function cancelRecord(record) {
  const reason = window.prompt("취소 사유를 입력해 주세요.", record.cancelReason || "");
  if (reason === null) return;
  try {
    await api.post(`/estimate/records/${record.id}/cancel`, { reason });
    await loadRecords();
  } catch (error) {
    console.error(error);
    alert("취소 처리 중 오류가 발생했습니다.");
  }
}

async function deleteRecord(record) {
  if (!confirm(`견적서 ${record.no} 항목을 목록에서 삭제할까요?`)) return;
  try {
    await api.delete(`/estimate/records/${record.id}`);
    await loadRecords();
  } catch (error) {
    console.error(error);
    alert("삭제 처리 중 오류가 발생했습니다.");
  }
}

async function viewEstimate(record) {
  const win = window.open("", "_blank");
  if (!win) {
    alert("팝업이 차단되었습니다. 팝업 허용 후 다시 시도해 주세요.");
    return;
  }
  win.document.write("<!doctype html><title>견적서 불러오는 중</title><p>견적서를 불러오고 있습니다.</p>");
  try {
    const detailRes = await api.get(`/estimate/records/${record.id}`);
    const payload = detailRes.data?.payload;
    if (!payload) throw new Error("estimate payload missing");
    const response = await api.post("/estimate/render", payload, {
      responseType: "text",
      params: { save: false },
      headers: { "Content-Type": "application/json" },
    });
    const blob = new Blob([response.data], { type: "text/html;charset=utf-8" });
    const blobUrl = URL.createObjectURL(blob);
    win.location.replace(blobUrl);
    window.setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
  } catch (error) {
    console.error(error);
    win.close();
    alert("견적서를 불러오지 못했습니다.");
  }
}

async function issueStatement(record) {
  const win = window.open("", "_blank");
  if (!win) {
    alert("팝업이 차단되었습니다. 팝업 허용 후 다시 시도해 주세요.");
    return;
  }
  win.document.write("<!doctype html><title>거래명세서 생성 중</title><p>거래명세서를 생성하고 있습니다.</p>");
  try {
    const response = await api.post(`/estimate/records/${record.id}/statement`, null, {
      responseType: "text",
    });
    const blob = new Blob([response.data], { type: "text/html;charset=utf-8" });
    const blobUrl = URL.createObjectURL(blob);
    win.location.replace(blobUrl);
    window.setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
    await loadRecords();
  } catch (error) {
    console.error(error);
    win.close();
    alert("거래명세서 발행 중 오류가 발생했습니다.");
  }
}

async function renderEstimate() {
  loading.value = true;
  const win = window.open("", "_blank");
  if (!win) {
    alert("팝업이 차단되었습니다. 팝업 허용 후 다시 시도해 주세요.");
    loading.value = false;
    return;
  }
  win.document.write("<!doctype html><title>견적서 생성 중</title><p>견적서를 생성하고 있습니다.</p>");
  try {
    const payload = JSON.parse(JSON.stringify(form));
    const receiverName = String(payload.receiver || "").trim();
    payload.customer = {
      ...payload.customer,
      company: receiverName,
      contactName: "",
    };
    payload.cutLabel = payload.roundDownBelowTenThousand ? "천원단위 절삭" : "";
    payload.cutAmount = 0;
    payload.bank = payload.supplier?.bank || createDefaultForm().supplier.bank;
    const response = editingRecordId.value
      ? await api.put(`/estimate/records/${editingRecordId.value}`, payload)
      : await api.post("/estimate/render", payload, {
          responseType: "text",
          headers: { "Content-Type": "application/json" },
        });
    let html = response.data;
    if (editingRecordId.value) {
      const renderPayload = JSON.parse(JSON.stringify(payload));
      const renderRes = await api.post("/estimate/render", renderPayload, {
        responseType: "text",
        params: { save: false },
        headers: { "Content-Type": "application/json" },
      });
      html = renderRes.data;
    }
    const blob = new Blob([html], { type: "text/html;charset=utf-8" });
    const blobUrl = URL.createObjectURL(blob);
    win.location.replace(blobUrl);
    window.setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
    await loadRecords();
  } catch (error) {
    console.error(error);
    win.close();
    alert("견적서 처리 중 오류가 발생했습니다.");
  } finally {
    loading.value = false;
  }
}
</script>

<style scoped>
.estimate-page {
  color: #17211c;
}

.estimate-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 16px;
}

h1,
h2,
p {
  margin: 0;
  letter-spacing: 0;
}

h1 {
  font-size: 28px;
  font-weight: 800;
}

h2 {
  font-size: 17px;
  font-weight: 800;
}

.estimate-header p {
  margin-top: 6px;
  color: #607168;
  font-size: 13px;
}

.actions,
.section-title-row,
.row-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

button {
  border: 1px solid #b8c7bd;
  border-radius: 6px;
  background: #fff;
  color: #17211c;
  cursor: pointer;
  font-weight: 700;
  min-height: 38px;
  padding: 0 14px;
}

button.primary {
  background: #1b7f6c;
  border-color: #1b7f6c;
  color: #fff;
}

button.secondary {
  background: #f6faf8;
}

button.danger {
  background: #fff5f5;
  border-color: #e0a5a5;
  color: #b42318;
}

button.compact {
  min-height: 32px;
  padding: 0 10px;
}

button:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.estimate-tabs {
  display: grid;
  grid-template-columns: repeat(3, 126px) 158px;
  gap: 8px;
  width: fit-content;
  max-width: 100%;
  border-bottom: 1px solid #d9e3dd;
  margin-bottom: 20px;
  padding-bottom: 10px;
}

.estimate-tabs button {
  width: 100%;
  min-height: 38px;
  padding: 0 10px;
  font-size: 13px;
  white-space: nowrap;
}

.estimate-tabs button.active {
  background: #1b7f6c;
  border-color: #1b7f6c;
  color: #fff;
}

.estimate-layout {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 260px;
  gap: 24px;
  align-items: start;
}

.estimate-form,
.form-band,
.manager-panel {
  display: grid;
  gap: 18px;
}

:deep(.manager-panel) {
  display: grid;
  gap: 16px;
  max-width: 1120px;
  width: 100%;
  margin: 0 auto;
}

:deep(.manager-panel .section-title-row) {
  align-items: center;
  gap: 8px;
}

:deep(.manager-panel .section-title-row button) {
  width: 88px;
  min-height: 36px;
  padding: 0 10px;
  font-size: 13px;
  white-space: nowrap;
}

:deep(.manager-panel .section-title-row button:first-of-type) {
  width: 104px;
}

:deep(.manager-panel h2) {
  margin-right: 8px;
  font-size: 20px;
}

:deep(.manager-panel button) {
  border: 1px solid #b8c7bd;
  border-radius: 6px;
  background: #fff;
  color: #17211c;
  cursor: pointer;
  font-weight: 700;
  min-height: 36px;
  padding: 0 12px;
}

:deep(.manager-panel button.primary) {
  background: #1b7f6c;
  border-color: #1b7f6c;
  color: #fff;
}

:deep(.manager-panel button.secondary) {
  background: #f6faf8;
}

:deep(.manager-panel button.danger) {
  background: #fff5f5;
  border-color: #e0a5a5;
  color: #b42318;
}

:deep(.manager-panel button:disabled) {
  opacity: 0.55;
  cursor: not-allowed;
}

:deep(.manager-grid) {
  display: grid;
  grid-template-columns: minmax(260px, 320px) minmax(0, 1fr);
  gap: 16px;
  align-items: start;
}

:deep(.manager-side) {
  display: grid;
  gap: 10px;
  position: sticky;
  top: 88px;
  padding: 14px;
  border: 1px solid #d9e3dd;
  border-radius: 8px;
  background: #f7faf8;
}

:deep(.manager-count) {
  color: #607168;
  font-size: 13px;
  font-weight: 700;
}

:deep(.manager-list) {
  display: grid;
  gap: 6px;
  max-height: 360px;
  overflow: auto;
  padding-right: 2px;
}

:deep(.manager-list-item) {
  display: grid;
  gap: 2px;
  width: 100%;
  min-height: 42px;
  padding: 8px 10px;
  text-align: left;
  border-color: #d3dfd8;
  background: #fff;
}

:deep(.manager-list-item.active) {
  border-color: #1b7f6c;
  background: #eaf6f2;
  color: #12624f;
}

:deep(.manager-list-item strong) {
  min-width: 0;
  font-size: 13px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

:deep(.manager-list-item span) {
  min-width: 0;
  color: #607168;
  font-size: 11px;
  font-weight: 700;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

:deep(.manager-form) {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px 16px;
  padding: 18px 20px;
  border: 1px solid #d9e3dd;
  border-radius: 8px;
  background: #fff;
}

:deep(.manager-panel label) {
  display: grid;
  gap: 6px;
  min-width: 0;
}

:deep(.manager-panel label.wide) {
  grid-column: span 2;
}

:deep(.stamp-editor.wide) {
  grid-column: span 2;
}

:deep(.manager-panel label span) {
  color: #607168;
  font-size: 12px;
  font-weight: 800;
}

:deep(.manager-panel input),
:deep(.manager-panel select) {
  width: 100%;
  min-width: 0;
  height: 40px;
  border: 1px solid #c8d4cd;
  border-radius: 6px;
  padding: 0 10px;
  background: #fff;
  color: #17211c;
  font: inherit;
}

:deep(.stamp-editor) {
  display: grid;
  grid-template-columns: 84px minmax(0, 1fr);
  gap: 10px 14px;
  align-items: center;
  padding: 12px;
  border: 1px solid #d9e3dd;
  border-radius: 8px;
  background: #f7faf8;
}

:deep(.stamp-title) {
  grid-column: 1 / -1;
  color: #607168;
  font-size: 12px;
  font-weight: 800;
}

:deep(.stamp-preview-img) {
  width: 76px;
  height: 76px;
  object-fit: contain;
  border: 1px solid #d9e3dd;
  border-radius: 6px;
  background: #fff;
}

:deep(.stamp-empty) {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 76px;
  height: 76px;
  border: 1px dashed #c8d4cd;
  border-radius: 6px;
  color: #607168;
  font-size: 12px;
  text-align: center;
}

:deep(.stamp-actions) {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

:deep(.stamp-actions input) {
  width: min(260px, 100%);
  height: auto;
  border: 0;
  padding: 0;
  background: transparent;
  font-size: 13px;
}

:deep(.stamp-actions input::file-selector-button) {
  min-height: 32px;
  margin-right: 8px;
  border: 1px solid #b8c7bd;
  border-radius: 6px;
  background: #fff;
  color: #17211c;
  cursor: pointer;
  font: inherit;
  font-weight: 700;
  padding: 0 10px;
}

.form-band {
  border-top: 1px solid #d9e3dd;
  padding-top: 18px;
}

.field-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
}

.supplier-grid {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

label {
  display: grid;
  gap: 6px;
  min-width: 0;
}

label.wide {
  grid-column: span 2;
}

label span,
.item-head,
.records-head {
  color: #607168;
  font-size: 12px;
  font-weight: 800;
}

input,
select {
  width: 100%;
  min-width: 0;
  height: 40px;
  border: 1px solid #c8d4cd;
  border-radius: 6px;
  padding: 0 10px;
  background: #fff;
  color: #17211c;
  font: inherit;
}

input.locked,
.readonly-line {
  background: #f3f7f5;
  color: #53645d;
}

.readonly-line {
  display: flex;
  align-items: center;
  width: 100%;
  min-height: 40px;
  border: 1px solid #c8d4cd;
  border-radius: 6px;
  padding: 0 10px;
  background: #f3f7f5;
  color: #17211c;
  font: inherit;
}

.cut-option {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  width: fit-content;
  margin-top: 4px;
  padding: 10px 12px;
  border: 1px solid #d9e3dd;
  border-radius: 6px;
  background: #f7faf8;
}

.cut-option input {
  width: 18px;
  height: 18px;
}

.cut-option span {
  color: #17211c;
  font-size: 13px;
}

.party-select {
  max-width: 420px;
}

.supplier-summary {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
  padding: 12px 14px;
  border: 1px solid #d9e3dd;
  border-radius: 6px;
  background: #f7faf8;
}

.supplier-summary span {
  display: block;
  color: #607168;
  font-size: 12px;
  font-weight: 800;
  margin-bottom: 4px;
}

.supplier-summary strong {
  font-size: 14px;
  font-weight: 800;
  color: #17211c;
}

.supplier-summary .wide {
  grid-column: 1 / -1;
}

.item-table,
.records-table {
  display: grid;
  gap: 8px;
}

.item-head,
.item-row {
  display: grid;
  grid-template-columns: 1.4fr 1fr 76px 76px 112px 1fr 40px;
  gap: 8px;
  align-items: center;
}

.icon-button {
  min-width: 38px;
  width: 38px;
  padding: 0;
  font-size: 22px;
  line-height: 1;
}

.total-panel {
  position: sticky;
  top: 94px;
  border-left: 1px solid #d9e3dd;
  padding-left: 20px;
}

.total-panel dl {
  display: grid;
  gap: 10px;
  margin: 18px 0 0;
}

.total-panel dl div {
  display: flex;
  justify-content: space-between;
  gap: 12px;
}

.total-panel dt {
  color: #607168;
  font-size: 13px;
  font-weight: 800;
}

.total-panel dd {
  margin: 0;
  font-weight: 800;
}

.total-panel .grand {
  border-top: 1px solid #d9e3dd;
  padding-top: 12px;
  font-size: 18px;
}

.record-filters {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 180px 90px;
  gap: 8px;
}

.records-head,
.records-row {
  display: grid;
  grid-template-columns: minmax(100px, 0.65fr) minmax(150px, 0.9fr) minmax(120px, 1fr) 115px minmax(170px, 0.9fr) minmax(360px, 0.9fr);
  gap: 8px;
  align-items: center;
}

.records-row {
  min-height: 48px;
  border-top: 1px solid #e3ebe6;
  padding: 10px 0 0;
}

.records-row > strong,
.records-row > span {
  min-width: 0;
  word-break: keep-all;
  overflow-wrap: anywhere;
}

.date-cell,
.status-cell {
  display: grid;
  gap: 4px;
  align-content: center;
}

.date-cell strong {
  font-size: 15px;
  font-weight: 800;
}

.date-cell small,
.status-cell small {
  color: #607168;
  font-size: 11px;
  font-weight: 700;
  line-height: 1.25;
}

.row-actions {
  justify-content: end;
  gap: 6px;
  flex-wrap: nowrap;
}

.row-actions button {
  flex: 0 0 auto;
  min-height: 30px;
  padding: 0 8px;
  font-size: 12px;
  white-space: nowrap;
}

.row-actions .action-short {
  width: 58px;
}

.row-actions .action-wide {
  width: 86px;
  font-size: 11px;
  letter-spacing: 0;
}

.status-pill {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 28px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 800;
  background: #e8f5ef;
  color: #12624f;
}

.status-pill.cancelled {
  background: #fff1f0;
  color: #b42318;
}

.status-pill.deleted {
  background: #f1f3f4;
  color: #5f6368;
}

.status-pill.statement {
  background: #e8f1fb;
  color: #195a9b;
}

.empty-text {
  color: #607168;
  padding: 20px 0;
}

@media (max-width: 980px) {
  .estimate-layout {
    grid-template-columns: 1fr;
  }

  :deep(.manager-grid) {
    grid-template-columns: 1fr;
  }

  :deep(.manager-side) {
    position: static;
  }

  :deep(.manager-list) {
    max-height: 180px;
  }

  .total-panel {
    position: static;
    border-left: 0;
    border-top: 1px solid #d9e3dd;
    padding: 18px 0 0;
  }

  .records-head {
    display: none;
  }

  .records-row {
    grid-template-columns: 1fr;
    gap: 8px;
    min-height: 0;
    padding: 14px;
    border: 1px solid #d9e3dd;
    border-radius: 8px;
    background: #fff;
  }

  .records-row > strong,
  .records-row > span {
    display: grid;
    grid-template-columns: 74px minmax(0, 1fr);
    gap: 8px;
    align-items: center;
  }

  .records-row > strong::before,
  .records-row > span::before {
    content: attr(data-label);
    color: #607168;
    font-size: 12px;
    font-weight: 800;
  }

  .date-cell,
  .status-cell {
    grid-template-columns: 74px minmax(0, 1fr);
  }

  .date-cell::before,
  .status-cell::before {
    grid-row: 1 / span 2;
  }

  .row-actions {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 8px;
  }

  .row-actions button {
    width: 100%;
  }

  .row-actions .action-short,
  .row-actions .action-wide {
    width: 100%;
  }
}

@media (max-width: 760px) {
  .estimate-header {
    align-items: stretch;
    flex-direction: column;
  }

  .actions {
    justify-content: flex-end;
  }

  .estimate-page {
    width: 100%;
    overflow-x: hidden;
  }

  .estimate-tabs {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .estimate-tabs button,
  .actions button {
    width: 100%;
  }

  .field-grid,
  .supplier-grid,
  .record-filters {
    grid-template-columns: 1fr;
  }

  :deep(.manager-form) {
    grid-template-columns: 1fr;
    padding: 14px;
  }

  :deep(.stamp-editor) {
    grid-template-columns: 1fr;
  }

  :deep(.stamp-preview-img),
  :deep(.stamp-empty) {
    width: 72px;
    height: 72px;
  }

  label.wide {
    grid-column: auto;
  }

  :deep(.manager-panel label.wide),
  :deep(.stamp-editor.wide) {
    grid-column: auto;
  }

  :deep(.manager-panel .section-title-row) {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  :deep(.manager-panel h2) {
    grid-column: 1 / -1;
    margin-right: 0;
  }

  :deep(.manager-panel button) {
    width: 100%;
  }

  .item-head {
    display: none;
  }

  .item-row {
    grid-template-columns: 1fr;
    border-top: 1px solid #e3ebe6;
    padding-top: 10px;
  }

  .item-row .icon-button {
    width: 100%;
  }

  .supplier-summary {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 520px) {
  .row-actions {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .estimate-tabs {
    grid-template-columns: 1fr;
  }

  .cut-option {
    width: 100%;
  }

  :deep(.manager-panel .section-title-row) {
    grid-template-columns: 1fr;
  }
}
</style>
